import { Star, ExternalLink, Quote } from "lucide-react";
import {
  GOOGLE_PROFILE_URL,
  GOOGLE_RATING,
  GOOGLE_REVIEWS,
} from "@/lib/business";

const Stars = ({ size = "w-4 h-4" }) => (
  <div className="flex gap-0.5" role="img" aria-label="Nota 5 de 5 estrelas">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${size} fill-amber-400 text-amber-400`} />
    ))}
  </div>
);

const GoogleIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
    />
  </svg>
);

export const Reviews = () => (
  <section
    id="avaliacoes"
    data-testid="reviews-section"
    className="scroll-mt-24 bg-stone-50"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
      <div className="max-w-2xl" data-reveal>
        <p
          data-testid="reviews-overline"
          className="text-xs font-bold uppercase tracking-[0.2em] text-brand"
        >
          Avaliações
        </p>
        <h2
          data-testid="reviews-heading"
          className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900"
        >
          Quem é cliente, recomenda
        </h2>
        <p
          data-testid="reviews-text"
          className="mt-5 text-base sm:text-lg leading-relaxed text-stone-600"
        >
          Avaliações reais publicadas pelos nossos clientes no perfil da Ótica
          Danko no Google.
        </p>
      </div>

      <div
        data-reveal
        data-testid="reviews-summary"
        className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6 rounded-2xl border border-stone-200 bg-white p-8 shadow-sm"
      >
        <div className="flex items-center gap-5">
          <span
            data-testid="reviews-score"
            className="font-heading text-5xl font-bold text-stone-900"
          >
            {GOOGLE_RATING.value}
          </span>
          <div>
            <Stars size="w-5 h-5" />
            <p
              data-testid="reviews-count"
              className="mt-1.5 text-sm text-stone-500"
            >
              {GOOGLE_RATING.count} avaliações no Google
            </p>
          </div>
        </div>
        <a
          href={GOOGLE_PROFILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="reviews-google-button"
          className="sm:ml-auto inline-flex items-center justify-center gap-2 rounded-full border border-stone-300 bg-white px-6 py-3 text-sm font-semibold text-stone-700 shadow-sm hover:border-brand hover:text-brand hover:shadow-md btn-motion focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        >
          <GoogleIcon className="w-4 h-4" />
          Ver avaliações no Google
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-6">
        {GOOGLE_REVIEWS.map((review, index) => (
          <figure
            key={review.text.slice(0, 24)}
            data-testid={`review-card-${index + 1}`}
            data-reveal
            style={{ transitionDelay: `${index * 100}ms` }}
            className="flex flex-col rounded-2xl border border-stone-200 bg-white p-7 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <Stars />
              <Quote className="w-5 h-5 text-brand-light" aria-hidden="true" />
            </div>
            <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-stone-700">
              “{review.text}”
            </blockquote>
            <figcaption className="mt-5 border-t border-stone-100 pt-4">
              <p className="text-sm font-semibold text-stone-900">
                {review.name || "Cliente da Ótica Danko"}
              </p>
              <p className="mt-0.5 text-xs text-stone-500">
                Avaliação no Google{review.time ? ` · ${review.time}` : ""}
              </p>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);
